import * as _ from 'lodash'
import { Variable, VBoolean, and, all } from 'alkali'
import VAbstractSource, { AbstractSource } from './AbstractSource'
import { Abstract, parseTaggedSentences } from './Abstract'
import { applyMetadata, diffObjects } from 'services/extract/util'
import { createSummaryUpdate, mapDesign } from 'services/extract/generate'
import StudyContext from 'components/extract/model/StudyContext'
import { submitExtractions } from 'components/extract/model/extraction'
import UpdateModel, { composeExtractionRequest } from 'components/extract/model/UpdateModel'
import DocSearch from 'components/extract/model/DocSearch'
import { createDocSearchAnnotations, createDSAbstractAnnotations } from 'components/extract/model/Annotators'
import { ExtractionData, ExtractionUpdate, StudyAnnotations, TaggedSentence } from 'services/extract/types'

const daToMetadata = dastudy => {
	let authors = dastudy.authors || []
	// authors can be assigned either a pipe-delimited string or an array
	if (!Array.isArray(authors)) {
		authors = authors.split('|')
	}
	return {
		Authors: authors?.length ? authors.join('|') : undefined,
		Acronym: dastudy.acronyms?.[0]
	}
}

const metadataToAbstractSource = (metadata = {}) => {
	let authors = metadata.Authors
	if (!!authors) {
		// authors can be assigned either a pipe-delimited string or an array
		if (!Array.isArray(authors)) {
			authors = authors.split('|')
		}
		authors = authors.join(',')
	}
	return {
		Title: metadata.Title,
		Authors: authors,
		Private: metadata.Private,
		Acronym: metadata.Acronym,
		Designs: _.uniq(metadata?.Designs || []).sort(),
		PublicationYear: metadata?.PublicationYear,
		Source: metadata?.Source,
		DatabaseType: metadata?.DatabaseType,
		DOI: metadata?.DOI,
		FullTextURL: metadata?.FullTextURL,
		AbstractSentences: metadata?.AbstractSentences,
		Abstract: metadata?.Abstract || (metadata?.AbstractSentences || []).map(({text}) => text).join('\n'),
		N: (metadata.N ?? '') + ''
	}
}

export const abstractSourceToMetadata = (src = {}, isUpload = false) => {
	src = _.cloneDeep(src)
	if (src.Designs) src.Designs = _.uniq(src.Designs.map(mapDesign))
	if (src.Authors) src.Authors = src.Authors.split(',').join('|')
	if (src.N != null) src.N = Number(src.N)
	if (isUpload && !src.Source) {
		src.Source  = 'Rapid Extract'
	}
	return src
}

/**
 * UpdateModel for Study Summary/Metadata. Fetches DocSearch annotations for absract/title text.
 */
export default class StudySummaryModel implements UpdateModel {
	readonly editingAbstract: VBoolean
	readonly allToggle = new VBoolean(false)
	readonly abstract: Variable<Abstract>
	readonly effectiveStudy
	readonly hasChanges: VBoolean
	readonly isValid: VBoolean
	readonly isSelfValid: VBoolean
	readonly update: Variable<AbstractSource>
	readonly canSubmit: VBoolean
	readonly initial = {}
	readonly existing = {}
	readonly annotations: Variable<StudyAnnotations>
	constructor(readonly abstractSource: VAbstractSource, readonly study, dsmetadata, readonly studyCtx: StudyContext, readonly onSubmit?) {
		if (study) {
			/* If we have a study, compose an "effective" study from, in reverse precedence:
			 * 1) DA study object
			 * 2) DD study object if present
			 * 3) DS study metadata
			 * 4) DA study metadata
			 * 5) DD study metadata
			 */
			const metadata = { ...dsmetadata }
			applyMetadata(metadata, _.defaults(studyCtx.study?.metadata, daToMetadata(study)))
			this.effectiveStudy = {
				...study,
				...studyCtx.study,
				metadata
			}
			this.initial = metadataToAbstractSource(metadata)
			abstractSource.put(this.initial)
			if (studyCtx.study) {
				this.existing = _.cloneDeep(metadataToAbstractSource(studyCtx.study.metadata))
			}
		}
		this.editingAbstract = new VBoolean(!study)
		this.update = abstractSource.to(src => this.getSummaryUpdate(this.existing, this.initial, src))
		this.hasChanges = this.update.to(update => !!createSummaryUpdate(Date.now(), update))
		this.abstract = all([abstractSource.property('AbstractSentences'), abstractSource.property('Abstract')], (sentences: TaggedSentence[], text: string) =>
			new Abstract(sentences, text)
		)
		this.annotations = all([abstractSource.Title, this.abstract], (title, abs: Abstract) =>
			DocSearch.findAnnotations(title, abs.text).then(({ title: titleAnnotations, abstract: abstractAnnotations }) => ({
				title: createDocSearchAnnotations(titleAnnotations, title),
				abstract: createDSAbstractAnnotations(abstractAnnotations, abs)
			}))
		)
		this.isSelfValid = abstractSource.validation.property('isValid')
		this.isValid = this.isSelfValid
		this.canSubmit = and(this.hasChanges, this.isValid)
	}

	/**
	 * Diff the current abstract source state to the base state
	 */
	protected getSummaryUpdate(base: AbstractSource, initial: AbstractSource, current: AbstractSource) {
		const diff: AbstractSource = diffObjects(base, current, {
			Designs: (a, b) => _.isEqual((a || []).sort(), (b || []).sort())
		})
		// only rederive AbstractSentences if we have changed Abstract from initial value
		if (initial.Abstract != current.Abstract) diff.AbstractSentences = parseTaggedSentences(diff.Abstract)
		return abstractSourceToMetadata(diff, !this.study)
	}

	generateUpdates(id) {
		const update = createSummaryUpdate(id, this.update.valueOf())
		return {
			updates: update ? [update] : []
		}
	}

	submit(debug = false) {
		if (!this.canSubmit.valueOf()) return
		const req = composeExtractionRequest(this.effectiveStudy, this.studyCtx.user, this)
		if (req) {
			return submitExtractions(req, this.onSubmit)
		}
	}

	reinit(extraction: ExtractionUpdate) {
		const data: ExtractionData = extraction.Data
		if (data.Summary?.Designs) this.abstractSource.property('Designs').put(_.uniq(data.Summary.Designs || []).sort())
		const n = data.Summary?.N || data.StudyN
		if (n) this.abstractSource.property('N').put(n + '')

		const acronym = data.Summary?.Acronym
		if (acronym) this.abstractSource.property('Acronym').put(acronym + '')

		// TODO: load other summary fields
	}
}